import React, { useEffect, useState, useCallback } from 'react';

import {
  Layout,
  Typography,
  Button,
  Space,
  Table,
  Input,
  Row,
  Col,
  Modal,
  Form,
  Select,
  Popconfirm,
  Tag
} from 'antd';
import {
  ReloadOutlined,
  SearchOutlined,
  PlusOutlined,
  EyeOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  CopyOutlined
} from '@ant-design/icons';
import moment from 'moment';

import useModuleGroupsListStore from '../../stores/useModuleGroupsListStore';
import { getAllCompanies } from '../../reducers/company/resources';
import mixpanel from '../../utils/mixpanel';
import { CATEGORY_LABELS } from '../templates/categories';
import TemplateMetaFields from './TemplateMetaFields';

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;

const mapSortOrder = (order) => {
  if (order === 'asc') return 'ascend';
  if (order === 'desc') return 'descend';
  return null;
};

const CreateCompanyAndTemplateFields = ({ createForm, companies }) => {
  const watchedCompanyId = Form.useWatch('company_id', createForm);
  const watchedIsTemplate = Form.useWatch('is_template', createForm);
  const companyDisabled = !!watchedIsTemplate;
  const templateDisabled = !!watchedCompanyId;

  return (
    <>
      <Form.Item
        name="company_id"
        label="Company"
        extra={companyDisabled ? "Templates can't be assigned to a company" : undefined}
      >
        <Select
          placeholder="Select a company (Optional)"
          allowClear
          showSearch
          optionFilterProp="children"
          filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
          options={companies.map(c => ({ value: c.id, label: c.name }))}
          disabled={companyDisabled}
        />
      </Form.Item>
      <TemplateMetaFields
        form={createForm}
        disabled={templateDisabled}
        disabledHint="Module-groups assigned to a company can't be templates"
      />
    </>
  );
};

const ModuleGroupV2 = ({ history }) => {
  const [createForm] = Form.useForm();
  const [duplicateForm] = Form.useForm();
  
  const {
    moduleGroups,
    loading,
    total,
    queryState,
    setQueryState,
    fetchModuleGroups,
    createModuleGroup,
    deleteModuleGroup,
    duplicateModuleGroup
  } = useModuleGroupsListStore();

  const [searchInput, setSearchInput] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [duplicateTargetId, setDuplicateTargetId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [companies, setCompanies] = useState([]);

  // Fetch initial data
  useEffect(() => {
    fetchModuleGroups();
  }, [queryState, fetchModuleGroups]);

  // Fetch companies for dropdown
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const res = await getAllCompanies();
        setCompanies(res.data || []);
      } catch (err) {
        console.error('Failed to fetch companies', err);
      }
    };
    fetchCompanies();
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      const nextSearch = searchInput.trim();
      setQueryState((prev) => {
        if (prev.search === nextSearch) return prev;
        return { ...prev, search: nextSearch, page: 1 };
      });
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput, setQueryState]);

  const handleTableChange = (pagination, filters, sorter) => {
    const tableSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const isSortAction = tableSorter && tableSorter.columnKey;

    setQueryState((prev) => {
      if (!isSortAction) {
        return {
          ...prev,
          page: pagination.current,
          pageSize: pagination.pageSize,
        };
      }

      const nextSortField = tableSorter.order ? (tableSorter.field || tableSorter.columnKey) : 'created_at';
      const nextSortOrder = tableSorter.order === 'ascend' ? 'asc' : 'desc';
      const sortChanged = prev.sortField !== nextSortField || prev.sortOrder !== nextSortOrder;

      return {
        ...prev,
        sortField: nextSortField,
        sortOrder: nextSortOrder,
        page: sortChanged ? 1 : pagination.current,
        pageSize: pagination.pageSize,
      };
    });
  };

  const handleCreateOpen = () => {
    createForm.resetFields();
    setIsCreateModalOpen(true);
    mixpanel.track('module group:create module group');
  };

  const handleCreateSubmit = async (values) => {
    setIsSubmitting(true);
    try {
      const result = await createModuleGroup({
        name: values.name,
        company_id: values.company_id || null,
        is_template: !!values.is_template,
        category: values.is_template ? values.category : null,
      });
      mixpanel.track('module group:confirm create module group', { data: { name: values.name, is_template: !!values.is_template, category: values.is_template ? values.category : null } });
      
      setIsCreateModalOpen(false);
      // Navigate to the newly created module group
      if (result && result.id) {
          history.replace(`/module-group/view/${result.id}`);
      } else {
          fetchModuleGroups();
      }
    } catch (error) {
      // Error handled by store
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDuplicateOpen = (record) => {
    duplicateForm.resetFields();
    duplicateForm.setFieldsValue({
        name: `Copy of ${record.name}`,
        company_id: record.company_id || null
    });
    setDuplicateTargetId(record.id);
    setIsDuplicateModalOpen(true);
    mixpanel.track('module group:open duplicate module group', { data: { originalId: record.id } });
  };

  const handleDuplicateSubmit = async (values) => {
    setIsSubmitting(true);
    try {
      const result = await duplicateModuleGroup(duplicateTargetId, {
        name: values.name,
        company_id: values.company_id || null
      });
      mixpanel.track('module group:confirm duplicate module group', { data: { originalId: duplicateTargetId, newName: values.name } });
      
      setIsDuplicateModalOpen(false);
      // Navigate to the newly duplicated module group
      if (result && result.id) {
          history.replace(`/module-group/view/${result.id}`);
      } else {
          fetchModuleGroups();
      }
    } catch (error) {
      // Error handled by store
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    mixpanel.track('module group:delete module group', { data: { selectedModGroupId: id } });
    await deleteModuleGroup(id);
  };

  const columns = [
    {
      title: 'Company',
      dataIndex: 'company_name',
      key: 'company_name',
      sorter: true,
      sortOrder: queryState.sortField === 'company_name' ? mapSortOrder(queryState.sortOrder) : null,
      render: (text) => text || '-',
      width: '18%',
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      sortOrder: queryState.sortField === 'name' ? mapSortOrder(queryState.sortOrder) : null,
      width: '18%',
    },
    {
      title: 'Template',
      key: 'is_template',
      sorter: true,
      sortOrder: queryState.sortField === 'is_template' ? mapSortOrder(queryState.sortOrder) : null,
      width: '14%',
      render: (_, record) => (
        record.is_template ? (
          <Space direction="vertical" size={0}>
            <Tag color="gold" style={{ marginRight: 0 }}>Template</Tag>
            {record.category && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                {CATEGORY_LABELS[record.category] || record.category}
              </Text>
            )}
          </Space>
        ) : null
      ),
    },
    {
      title: 'Modules',
      dataIndex: 'modules_count',
      key: 'modules_count',
      sorter: true,
      sortOrder: queryState.sortField === 'modules_count' ? mapSortOrder(queryState.sortOrder) : null,
      render: (val) => val || 0,
      width: '10%',
    },
    {
      title: 'Creation Date',
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: true,
      sortOrder: queryState.sortField === 'created_at' ? mapSortOrder(queryState.sortOrder) : null,
      render: (val) => val ? moment(val).format('MMM DD, YYYY') : '-',
      width: '15%',
    },
    {
      title: 'Action',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => {
              mixpanel.track('module group:change route', { url: `/module-group/view/${record.id}` });
              history.push(`/module-group/view/${record.id}`);
            }}
          >
            View
          </Button>
          <Button
            size="small"
            icon={<CopyOutlined />}
            onClick={() => handleDuplicateOpen(record)}
          >
            Duplicate
          </Button>
          <Popconfirm
            title="Delete the module group"
            description={`Are you sure to delete ${record.name}?`}
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              danger
              size="small"
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Layout className="gh-compact-page">
      <Content className="gh-compact-panel" style={{ overflowY: 'auto' }}>

        <div style={{ padding: '20px 20px 0', marginBottom: 16 }}>
          <Row gutter={[16, 16]} align="middle" justify="space-between">
            <Col xs={24} md={8}>
              <Title level={3} style={{ margin: 0, color: '#1f2937' }}>
                Existing Module Groups
              </Title>
            </Col>
            <Col xs={24} md={16} style={{ textAlign: 'right' }}>
              <Space wrap>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleCreateOpen}
                >
                  Create Module Group
                </Button>
              </Space>
            </Col>
          </Row>
        </div>

        <div style={{ padding: '0 20px 20px' }}>
          <div className="gh-compact-toolbar" style={{ marginBottom: 8 }}>
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} md={10}>
                <Input
                  placeholder="Search by module group or company name"
                  prefix={<SearchOutlined />}
                  allowClear
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  disabled={loading}
                />
              </Col>
            </Row>
          </div>

          <div className="gh-compact-table-wrap" style={{ background: '#fff' }}>
            {queryState.search && (
              <div style={{ marginBottom: 12 }}>
                <Text type="secondary">
                  Showing matches for "{queryState.search}"
                </Text>
              </div>
            )}
            <Table
              className="gh-themed-table"
              rowKey="id"
              columns={columns}
              dataSource={moduleGroups}
              loading={loading}
              size="small"
              onChange={handleTableChange}
              expandable={{
                expandedRowRender: (record) => (
                  <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    {record.description && (
                      <div>
                        <Text strong style={{ fontSize: 12 }}>Description</Text>
                        <Paragraph
                          type="secondary"
                          style={{ margin: '2px 0 0', whiteSpace: 'pre-wrap', fontSize: 13 }}
                          ellipsis={{ rows: 3, expandable: true }}
                        >
                          {record.description}
                        </Paragraph>
                      </div>
                    )}
                    <div>
                      <Text strong style={{ fontSize: 12 }}>Initial message</Text>
                      <Paragraph
                        type="secondary"
                        style={{ margin: '2px 0 0', whiteSpace: 'pre-wrap', fontSize: 13 }}
                        ellipsis={{ rows: 4, expandable: true }}
                      >
                        {record.initial_message_response || 'No initial message'}
                      </Paragraph>
                    </div>
                  </Space>
                ),
                rowExpandable: (record) => !!(record.description || record.initial_message_response),
              }}
              pagination={{
                current: queryState.page,
                pageSize: queryState.pageSize,
                total: total,
                showSizeChanger: true,
                showTotal: (t, range) => `${range[0]}-${range[1]} of ${t}`,
              }}
            />
          </div>
        </div>

        <Modal
          title="Create Module Group"
          open={isCreateModalOpen}
          onCancel={() => setIsCreateModalOpen(false)}
          onOk={() => createForm.submit()}
          okText="Submit"
          okButtonProps={{ loading: isSubmitting }}
          destroyOnClose
        >
          <Form
            layout="vertical"
            form={createForm}
            onFinish={handleCreateSubmit}
            initialValues={{ is_template: false }}
          >
            <Form.Item
              name="name"
              label="Module Group Name"
              rules={[{ required: true, message: 'Module Group name cannot be blank' }]}
            >
              <Input placeholder="Enter Module Group name" />
            </Form.Item>
            <CreateCompanyAndTemplateFields createForm={createForm} companies={companies} />
          </Form>
        </Modal>

        <Modal
          title="Duplicate Module Group"
          open={isDuplicateModalOpen}
          onCancel={() => setIsDuplicateModalOpen(false)}
          onOk={() => duplicateForm.submit()}
          okText="Duplicate"
          okButtonProps={{ loading: isSubmitting }}
          destroyOnClose
        >
          <Form
            layout="vertical"
            form={duplicateForm}
            onFinish={handleDuplicateSubmit}
          >
            <Form.Item
              name="name"
              label="New Module Group Name"
              rules={[{ required: true, message: 'Module Group name cannot be blank' }]}
            >
              <Input placeholder="Enter Module Group name" />
            </Form.Item>
            <Form.Item
              name="company_id"
              label="Company"
            >
              <Select
                placeholder="Select a company (Optional)"
                allowClear
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                options={companies.map(c => ({ value: c.id, label: c.name }))}
              />
            </Form.Item>
          </Form>
        </Modal>

      </Content>
    </Layout>
  );
};

export default ModuleGroupV2;
